import { clearDraftBackup } from "../../save/draftBackup";
import { fromBackendToDraft } from "./commandeDraft";

// Rapprochement du brouillon local avec la commande renvoyée par le serveur ou gardée en secours.

export function findMatchingPatient(patient, patients) {
    return (patients || []).find(candidate =>
        patient?._clientKey && candidate._clientKey === patient._clientKey
    ) || (patients || []).find(candidate =>
        patient?.commandePatientId && candidate.commandePatientId === patient.commandePatientId
    ) || (patients || []).find(candidate =>
        patient?.id && candidate.id === patient.id
    ) || null;
}

export function findMatchingProduit(produit, produits) {
    return (produits || []).find(candidate =>
        produit?._clientKey && candidate._clientKey === produit._clientKey
    ) || (produits || []).find(candidate =>
        produit?.id && candidate.id === produit.id
    ) || null;
}

const PHARMA_PATIENT_FIELDS = [
    "commandePatientId", "id", "nom", "prenom", "dateNaissance", "classification",
    "commentaryProductionRobot", "commentaryProductionEtiquetage",
];
const PHARMA_PRODUIT_FIELDS = [
    "id", "nom", "dosage", "posologie", "quantite", "destination", "statut", "siBesoin", "classificationMedicament",
    "deliveryDate", "billingHourTime", "billingValidated", "billingUsername", "planificationValidated",
    "planificationValidatedAt", "planificationValidatedBy", "commentaryPlanificationRobot",
    "commentaryPlanificationEtiquetage", "ehpadWarnedIfOutOfStock",
];

function sameFields(left, right, fields) {
    return fields.every(field => left[field] === right[field]);
}

// Patient envoyé « inchangé » au serveur : mêmes champs et mêmes produits que la version enregistrée.
export function samePharmaPatientPayload(current, original) {
    if (!current || !original) return false;
    if (!sameFields(current, original, PHARMA_PATIENT_FIELDS)) return false;

    const currentProduits = current.produits || [];
    const originalProduits = original.produits || [];
    if (currentProduits.length !== originalProduits.length) return false;

    return currentProduits.every(currentProduit => {
        const originalProduit = findMatchingProduit(currentProduit, originalProduits);
        return Boolean(originalProduit) && sameFields(currentProduit, originalProduit, PHARMA_PRODUIT_FIELDS);
    });
}

const takeSavedUnlessChanged = (currentValue, sentValue, savedValue) =>
    Object.is(currentValue, sentValue) ? savedValue : currentValue;

function mergeFields(current, sent, saved, fields) {
    return Object.fromEntries(fields.map(field => [field, takeSavedUnlessChanged(current[field], sent[field], saved[field])]));
}

const MERGED_PRODUIT_FIELDS = [
    "nom", "codeCis", "cip13", "bdpmDisponibiliteStatutCodes", "bdpmDisponibiliteStatuts", "dosage", "posologie",
    "quantite", "destination", "statut", "siBesoin", "classificationMedicament",
];
const MERGED_PATIENT_FIELDS = [
    "nom", "prenom", "dateNaissance", "classification", "commentaryProductionRobot", "commentaryProductionEtiquetage",
];

function findByClientKeyOrIndex(item, index, candidates) {
    const keyIndex = candidates.findIndex(candidate => item._clientKey && candidate._clientKey === item._clientKey);
    return keyIndex >= 0 ? keyIndex : index;
}

function mergeSavedProduit(produit, produitIndex, sentProduits, savedProduits) {
    const matchedProduitIndex = findByClientKeyOrIndex(produit, produitIndex, sentProduits);
    const sentProduit = sentProduits[matchedProduitIndex];
    const savedProduit = savedProduits.find(candidate =>
        produit._clientKey && candidate._clientKey === produit._clientKey
    ) || savedProduits.find(candidate =>
        produit.id != null && candidate.id === produit.id
    ) || savedProduits[matchedProduitIndex];
    if (!sentProduit || !savedProduit) return produit;

    return {
        ...produit,
        id: produit.id ?? savedProduit.id ?? null,
        ...mergeFields(produit, sentProduit, savedProduit, MERGED_PRODUIT_FIELDS),
    };
}

function findSavedPatient(patient, matchedIndex, savedPatients) {
    return savedPatients.find(candidate =>
        patient._clientKey && candidate._clientKey === patient._clientKey
    ) || savedPatients.find(candidate =>
        patient.commandePatientId && candidate.commandePatientId === patient.commandePatientId
    ) || (!patient._clientKey ? savedPatients[matchedIndex] : null);
}

function mergeSavedPatient(patient, patientIndex, sentPatients, savedPatients) {
    const matchedIndex = findByClientKeyOrIndex(patient, patientIndex, sentPatients);
    const sentPatient = sentPatients[matchedIndex];
    const savedPatient = findSavedPatient(patient, matchedIndex, savedPatients);
    if (!sentPatient || !savedPatient) return patient;

    const sentProduits = sentPatient.produits || [];
    const savedProduits = savedPatient.produits || [];
    const produits = (patient.produits || []).map((produit, produitIndex) =>
        mergeSavedProduit(produit, produitIndex, sentProduits, savedProduits));

    return {
        ...patient,
        // Le choix ne vaut que pour cette tentative de sauvegarde. Le
        // conserver ferait appliquer silencieusement la même décision à
        // une future modification d'identité ou de date.
        _identityResolution: undefined,
        _identityCandidateId: undefined,
        _createdInCurrentDraft: sentPatient.id == null
            ? savedPatient._createdInCurrentDraft === true
            : patient._createdInCurrentDraft === true,
        commandePatientId: patient.commandePatientId ?? savedPatient.commandePatientId ?? null,
        id: patient.id ?? savedPatient.id ?? null,
        ...mergeFields(patient, sentPatient, savedPatient, MERGED_PATIENT_FIELDS),
        pharmaceuticalValidated: savedPatient.pharmaceuticalValidated ?? patient.pharmaceuticalValidated ?? false,
        produits,
    };
}

export function mergeSavedDraft(current, sent, saved) {
    if (!current || !saved) return current;

    const sentPatients = sent?.patients || [];
    const savedPatients = saved.patients || [];
    const patients = (current.patients || []).map((patient, patientIndex) =>
        mergeSavedPatient(patient, patientIndex, sentPatients, savedPatients));

    return {
        ...current,
        statut: takeSavedUnlessChanged(current.statut, sent?.statut, saved.statut),
        destination: takeSavedUnlessChanged(current.destination, sent?.destination, saved.destination),
        source: takeSavedUnlessChanged(current.source, sent?.source, saved.source),
        serviceKey: takeSavedUnlessChanged(current.serviceKey, sent?.serviceKey, saved.serviceKey),
        commentaire: takeSavedUnlessChanged(current.commentaire, sent?.commentaire, saved.commentaire),
        emailEmetteur: takeSavedUnlessChanged(current.emailEmetteur, sent?.emailEmetteur, saved.emailEmetteur),
        patients,
    };
}

export function attachPatientClientKeys(savedCommande, results) {
    if (!savedCommande) return savedCommande;
    const successfulResults = (results || []).filter(result => result.saved && result.patient);
    const patientIdCounts = (savedCommande.patients || []).reduce((counts, patient) => {
        if (patient.id) counts.set(patient.id, (counts.get(patient.id) || 0) + 1);
        return counts;
    }, new Map());
    return {
        ...savedCommande,
        patients: (savedCommande.patients || []).map(patient => {
            const result = successfulResults.find(candidate =>
                (candidate.patient.commandePatientId
                    && candidate.patient.commandePatientId === patient.commandePatientId)
                || (
                    candidate.patient.id
                    && candidate.patient.id === patient.id
                    && patientIdCounts.get(patient.id) === 1
                )
            );
            if (!result) return patient;

            const resultProduits = result.patient?.produits || [];
            return {
                ...patient,
                _clientKey: result.clientKey,
                _createdInCurrentDraft: result.patientCreated === true,
                produits: (patient.produits || []).map(produit => {
                    const confirmedProduit = resultProduits.find(candidate =>
                        produit.id != null && candidate.id === produit.id
                    );
                    return confirmedProduit?.clientKey
                        ? { ...produit, clientKey: confirmedProduit.clientKey }
                        : produit;
                }),
            };
        }),
    };
}

export function normalizedPatientIdentity(patient) {
    const normalize = value => String(value ?? "").trim().toLocaleLowerCase("fr");
    return [
        normalize(patient?.nom),
        normalize(patient?.prenom),
        patient?.dateNaissance ?? "",
    ].join("|");
}

export function samePersistedPatient(left, right) {
    if (left?.commandePatientId != null && right?.commandePatientId != null) {
        return left.commandePatientId === right.commandePatientId;
    }
    if (left?.id != null && right?.id != null) return left.id === right.id;
    return normalizedPatientIdentity(left) === normalizedPatientIdentity(right);
}

export function matchesPersistedProduitIdentity(left, right) {
    if (left?.id != null && right?.id != null) return left.id === right.id;
    return left?.nom === right?.nom && left?.dosage === right?.dosage;
}

export function mergeBackupWithPersistedPatients(backupDraft, persistedDraft) {
    const backupPatients = backupDraft?.patients || [];
    const persistedPatients = persistedDraft?.patients || [];

    const mergedPersistedPatients = persistedPatients.map(persistedPatient => {
        const backupPatient = backupPatients.find(candidate =>
            samePersistedPatient(candidate, persistedPatient)
        );
        if (!backupPatient) return persistedPatient;

        const backupProduits = backupPatient.produits || [];
        const persistedProduits = persistedPatient.produits || [];
        const missingPersistedProduits = persistedProduits.filter(persistedProduit =>
            !backupProduits.some(candidate => matchesPersistedProduitIdentity(candidate, persistedProduit))
        );

        return {
            ...persistedPatient,
            ...backupPatient,
            commandePatientId: backupPatient.commandePatientId ?? persistedPatient.commandePatientId,
            id: backupPatient.id ?? persistedPatient.id,
            produits: [...backupProduits, ...missingPersistedProduits],
        };
    });
    const localOnlyPatients = backupPatients.filter(backupPatient =>
        !persistedPatients.some(candidate => samePersistedPatient(backupPatient, candidate))
    );

    return {
        ...persistedDraft,
        ...backupDraft,
        patients: [...mergedPersistedPatients, ...localOnlyPatients],
    };
}

export function usableDraftBackupForCommande(commande, backup) {
    if (!backup?.draft) return null;
    if (commande?.statut === "VALIDE") {
        clearDraftBackup(commande.id);
        return null;
    }
    return {
        ...backup,
        draft: mergeBackupWithPersistedPatients(
            backup.draft,
            fromBackendToDraft(commande)
        ),
    };
}
